import { Suspense, lazy, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { ASK_COLUMN_MIN, askOverlays, deskAccess, deskCanonical, deskHead, deskTarget, railScreens, type DeskScreen } from '../lib/desk'
import { useIsAdmin } from '../lib/useIsAdmin'
import { AccountContext } from '../components/ScreenHead'
import { Toast } from '../ui'
import { City } from '../screens/City'
import { Rail } from './Rail'
import { AskPanel, AskAboutContext } from './AskPanel'
import { Year } from './screens/Year'
import { Browse } from './screens/Browse'
import { Lists } from './screens/Lists'
import { Health } from './screens/Health'
import { Account } from './screens/Account'
import { Boundary } from '../components/Boundary'
import { userKey } from '../lib/userStorage'
import './desk.css'

const DevUi = lazy(() => import('../screens/DevUi').then(m => ({ default: m.DevUi })))

const ASK_KEY = 'kiez-desk-ask'
// Per user (U0.4): whether Ask is open is a choice about one person's day, and
// `clearUserStorage` removes it on their sign-out. Read from the stored session
// rather than `useAuth()` because the shell paints before `getSession()`
// resolves; the stored id is the one that session will confirm.
const askKey = () => userKey(ASK_KEY)
const readAsk = () => { try { return localStorage.getItem(askKey()) === '1' } catch { return false } }
const writeAsk = (open: boolean) => { try { localStorage.setItem(askKey(), open ? '1' : '0') } catch { /* private window */ } }

const TITLE: Record<DeskScreen, string> = {
  year: 'Year', browse: 'Browse', lists: 'Lists', health: 'Health', account: 'Account',
}
const SURFACE: Record<DeskScreen, () => JSX.Element> = {
  year: Year, browse: Browse, lists: Lists, health: Health, account: Account,
}

/** The raw first segment of the hash, subscribed to `hashchange`. Not
 *  `useRoute().tab`: the router drops heads it does not know, and Health and
 *  Account are two of them. */
function useHead(): string {
  return useSyncExternalStore(
    cb => { window.addEventListener('hashchange', cb); return () => window.removeEventListener('hashchange', cb) },
    () => deskHead(window.location.hash),
  )
}

/** Whether Ask is laid over the surface (below `ASK_COLUMN_MIN`). */
const OVERLAY = `(max-width: ${ASK_COLUMN_MIN - 1}px)`
function useAskOverlays(): boolean {
  return useSyncExternalStore(
    cb => { const mq = window.matchMedia(OVERLAY); mq.addEventListener('change', cb); return () => mq.removeEventListener('change', cb) },
    () => window.matchMedia(OVERLAY).matches,
  )
}

/** Replace the hash without adding a history entry. */
function rewrite(hash: string) {
  history.replaceState(null, '', hash)
  window.dispatchEvent(new HashChangeEvent('hashchange'))
}

/** The desktop shell: rail, working surface, Ask panel. Shares every query and
 *  cache with the phone; the two never mount at once. Every screen is a hash. */
export function Shell({ showDot }: { showDot: boolean }) {
  const rawHead = useHead()
  // U0.3: Health and the specimen are admin screens. A non-admin who types
  // one is sent where `deskAccess` says; while the answer is not in, nothing
  // is drawn and nothing is rewritten, so Doug's reload on Health stays put.
  const isAdmin = useIsAdmin()
  const access = deskAccess(rawHead, isAdmin)
  const waiting = access.access === 'wait'
  const head = access.access === 'wait' ? rawHead : access.head
  const target = deskTarget(waiting ? 'year' : head)
  // Remembered open, it is a column on a wide window; on a narrow one it
  // would open laid over the screen on every load, so there it starts shut.
  const [askOpen, setAskOpen] = useState(() => readAsk() && !askOverlays(window.innerWidth))
  // The panel mounts on first open and then stays, so a turn streaming while
  // it is hidden is still there when it comes back.
  const [askMounted, setAskMounted] = useState(askOpen)
  const [seed, setSeed] = useState('')
  // The last real screen, which `#/butler` opens the panel over.
  const [last, setLast] = useState<DeskScreen>('year')

  const setAsk = (open: boolean) => {
    setAskOpen(open)
    if (open) setAskMounted(true)
    writeAsk(open)
  }

  const askAbout = useCallback((text: string) => {
    setSeed(text)
    setAsk(true)
  }, [])
  const seeded = useCallback(() => setSeed(''), [])

  useEffect(() => {
    if (waiting) return
    if (target.screen) {
      setLast(target.screen)
      // `#/today`, `#/home`, an unknown head, or nothing: say where we are.
      if (rawHead !== target.screen) rewrite(deskCanonical(window.location.hash, target.screen))
    } else if (target.ask) {
      // `#/butler` opens the panel over the current screen.
      setAsk(true)
      rewrite(`#/${last}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawHead, head, waiting])

  const screen: DeskScreen | null = waiting || target.phone ? null : target.screen ?? last
  const Surface = screen ? SURFACE[screen] : null
  const accountCtx = useMemo(() => ({ open: () => { window.location.hash = '#/account' }, dot: showDot }), [showDot])

  const nav = (s: DeskScreen) => { window.location.hash = `#/${s}` }

  // Laid over the surface, Ask is a drawer: a press on the surface puts it
  // away, so the row clicked beside it opens where it can be seen.
  const overlays = useAskOverlays()
  const putAway = overlays && askOpen ? () => setAsk(false) : undefined

  return (
    <AccountContext.Provider value={accountCtx}>
      <div className={`desk${askOpen ? '' : ' desk--ask-closed'}`}>
        <Rail screens={railScreens(isAdmin)} current={screen} showDot={showDot} askOpen={askOpen} onNav={nav} onAsk={() => setAsk(!askOpen)} />
        <main className="desk-main" onPointerDown={putAway}>
          <header className="desk-head">
            <h1>{screen ? TITLE[screen] : waiting ? '' : target.phone === 'city' ? 'City' : 'Specimen'}</h1>
          </header>
          <div className="desk-surface">
            <AskAboutContext.Provider value={askAbout}>{Surface && <Boundary where={`desk-${screen}`}><Surface /></Boundary>}</AskAboutContext.Provider>
            {!waiting && target.phone === 'city' && <div className="desk-phone desk-phone--screen"><Boundary where="desk-city"><City /></Boundary></div>}
            {!waiting && target.phone === 'dev' && <div className="desk-phone desk-phone--screen"><Boundary where="desk-dev"><Suspense fallback={null}><DevUi /></Suspense></Boundary></div>}
          </div>
        </main>
        {askMounted && <Boundary where="desk-ask"><AskPanel open={askOpen} onHide={() => setAsk(false)} seed={seed} onSeeded={seeded} /></Boundary>}
      </div>
      <Toast />
    </AccountContext.Provider>
  )
}
